import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import useModal from "hooks/modals/useModal";

import Page from "components/page/Page";
import { ButtonNew as Button } from "components/buttonNew";
import FormLayout from "components/form/FormLayout";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import {
  GetLeagueCompetitionFormat,
  DeleteLeagueCompetitionFormat,
} from "api/rpc/2024-04/facilityAdmin/league/scoring/format";
import { GetLeagueScoresheets, DeleteLeagueScoresheet } from "api/rpc/2024-04/facilityAdmin/league/scoring/scoresheet";
import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { TLeagueRoundFormat, TLeagueScoresheet, ILeague } from "redux/reducers/models/league";
import { valueToString } from "helpers/Helpers";
import Popup from "components/popup/Popup";
import Input from "components/form/input/Input";
import CopyLink from "components/copyLink/CopyLink";

export default function LeagueRoundOverview() {
  const history = useHistory();
  const { t } = useTranslation();
  const { leagueId, scoresheetId } = useParams<{ leagueId: string; scoresheetId: string }>();

  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const [league, setLeague] = useState<ILeague>(undefined);
  const [scoresheet, setScoresheet] = useState<TLeagueScoresheet>(undefined);
  const [scoringFormats, setScoringFormats] = useState<TLeagueRoundFormat[]>(undefined);

  const {
    state: deleteScoresheetPopup,
    updateModal: updateDeleteScoresheetPopup,
    closeModal: closeDeleteScoresheetPopup,
  } = useModal();

  const {
    state: deleteFormatPopup,
    updateModal: updateDeleteFormatPopup,
    closeModal: closeDeleteFormatPopup,
  } = useModal({
    format_id: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadleague(source.token);
    void loadScoresheet(scoresheetId, source.token);
    void loadScoringFormats(scoresheetId, source.token);
    return () => source.cancel();
  }, [scoresheetId]);

  async function loadleague(token?: CancelToken) {
    const res = await GetLeague({ id: leagueId }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading league.")));
      return;
    }

    setLeague(res.data[0]);
  }

  async function loadScoresheet(leagueScoresheetId: string | number, token?: CancelToken) {
    const res = await GetLeagueScoresheets({ id: leagueScoresheetId }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading scoresheet.")));
      return;
    }

    setScoresheet(res.data[0]);
  }

  async function loadScoringFormats(leagueScoresheetId: string | number, token?: CancelToken) {
    if (scoringFormats !== undefined) {
      setScoringFormats(undefined);
    }
    const res = await GetLeagueCompetitionFormat(
      { league_scoresheet_id: leagueScoresheetId },
      token ? false : true,
      token,
    );
    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading round formats.")));
    }

    setScoringFormats(res.status !== StatusCode.OK ? [] : res.data);
  }

  async function deleteScoresheet() {
    const res = await DeleteLeagueScoresheet({ id: scoresheetId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting scoresheet.")));
      return;
    }

    dispatch(showSuccess(t("Scoresheet deleted successfully")));
    history.push(`/admin/league/${leagueId}/rounds`);
  }

  async function deleteScoringFormat(formatId: number) {
    const res = await DeleteLeagueCompetitionFormat({ format_id: formatId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting format.")));
      return;
    }

    dispatch(showSuccess(t("Format deleted successfully")));
    void closeDeleteFormatPopup();
    void loadScoringFormats(scoresheetId);
  }

  async function writeTextToClipBoard(link: string) {
    await navigator.clipboard.writeText(link);
    dispatch(showSuccess(t("Copied link to clipboard")));
  }

  const primaryAction = {
    content: t("Edit"),
    action: () => history.push(`/admin/league/${leagueId}/rounds/scoresheet/${scoresheetId}/edit`),
  };

  const secondaryActions = [
    {
      content: t("Delete"),
      action: () => updateDeleteScoresheetPopup({ isOpen: true }),
    },
  ];

  return (
    <Page
      title={`${scoresheet ? scoresheet?.name : t("Round")} ${t("Overview")}`}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Rounds"),
          url: `/admin/league/${leagueId}/rounds`,
        },
      ]}
    >
      <Card>
        <Card.Section title={t("Details")}>
          <FormLayout>
            <FormLayout.Group>
              <Input label={t("Front Division")} disabled={true} value={scoresheet?.front_division_title} />
              <Input label={t("Back Division")} disabled={true} value={scoresheet?.back_division_title} />
              <Input disabled={true} value={`${scoresheet?.participation} ${t("Rounds Played")}`} />
            </FormLayout.Group>
            <FormLayout.Group>
              <CopyLink
                label={t("Public Leaderboard Link")}
                link={
                  window.origin +
                  `/league/${facilityStore.facility?.short_name}/${league?.handle}/scoring/results/${scoresheetId}`
                }
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
        <Card.Section title={t("Actions")}>
          <div className="flex justify-between">
            <div className="flex justify-start">
              <Button
                onClick={() => history.push(`/admin/league/${leagueId}/rounds/scoresheet/${scoresheetId}/scores`)}
              >
                {t('Enter Scores')}
              </Button>
              <Button
                onClick={() => history.push(`/admin/league/${leagueId}/rounds/scoresheet/${scoresheetId}/leaderboard`)}
              >
                {t('Results')}
              </Button>
            </div>
            <Button
              onClick={() =>
                history.push(`/admin/league/${leagueId}/rounds/scoresheet/${scoresheetId}/scoring-formats/new`)
              }
            >
              {t('New Scoring Format')}
            </Button>
          </div>
        </Card.Section>
        <Card.Section title={t("Scoring Formats")}>
          <DataTable
            columns={[
              { label: t("Title") },
              { label: t("Organization") },
              { label: t("Holes") },
              { label: "", width: "3%" },
              { label: "", width: "3%" },
            ]}
            loading={scoringFormats === undefined}
          >
            {scoringFormats?.map(format => (
              <tr key={format.id}>
                <td>{format.title}</td>
                <td>{valueToString(format.organization)}</td>
                <td>{valueToString(format.holes)}</td>
                <td>
                  <Button
                    type="tertiary"
                    onClick={() =>
                      history.push(
                        `/admin/league/${leagueId}/rounds/scoresheet/${scoresheetId}/scoring-formats/${format.id}`,
                      )
                    }
                  >
                    <FontAwesomeIcon icon={["far", "pen-to-square"]} />
                  </Button>
                </td>
                <td>
                  <Button
                    type="tertiary"
                    onClick={() => updateDeleteFormatPopup({ isOpen: true, format_id: format.id })}
                  >
                    <FontAwesomeIcon icon={["far", "trash"]} />
                  </Button>
                </td>
              </tr>
            ))}
          </DataTable>
        </Card.Section>
      </Card>

      <Popup
        type="warning"
        closable
        open={deleteScoresheetPopup.isOpen}
        onCancel={closeDeleteScoresheetPopup}
        onOk={deleteScoresheet}
        title={t("Delete Round?")}
        description={t("Are you sure you want to delete this round?")}
      />

      <Popup
        type="warning"
        closable
        open={deleteFormatPopup.isOpen}
        onCancel={closeDeleteFormatPopup}
        onOk={() => deleteScoringFormat(deleteFormatPopup.format_id)}
        title={t("Delete Scoring Format?")}
        description={t("Are you sure you want to delete this scoring format?")}
      />
    </Page>
  );
}
